/* global runtime */
// Virksomhetsprofil, logo, passord og ansattportal-adaptere.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const requestBusinessDeletion = async (businessId = runtime.activeBusinessId) => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  const payload = await authorizedPost('requestBusinessDeletionHttp', { businessId });
  await loadBusinessAccess();
  emitRuntime();
  return payload;
};
const publicPost = async (functionName, body = {}) => {
  const response = await fetch(functionUrl(functionName), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const responseText = await response.text();
  const payload = responseText.trim() ? JSON.parse(responseText) : null;
  if (!response.ok) throw new Error(payload?.message || payload?.error || responseText || `HTTP ${response.status}`);
  return payload;
};

const logoExtensionFromFileName = (name = '') => String(name || '').toLowerCase().split('.').pop() || '';

const normalizeBusinessLogoFile = (file = {}) => {
  const cleanFileName = safeFilePart(file.name || 'firmalogo');
  const extension = logoExtensionFromFileName(cleanFileName);
  const rawType = String(file.type || '').trim().toLowerCase();
  const contentType = rawType === 'image/x-png'
    ? 'image/png'
    : (BUSINESS_LOGO_MIME_BY_EXTENSION[extension] || rawType);
  const allowed = Object.values(BUSINESS_LOGO_MIME_BY_EXTENSION).includes(contentType);
  const size = Number(file.size || 0);
  if (!allowed) {
    return { ok: false, cleanFileName, contentType, message: 'Velg PNG, JPG eller WebP som firmalogo.' };
  }
  if (size <= 0) {
    return { ok: false, cleanFileName, contentType, message: 'Logo-filen er tom.' };
  }
  if (size > BUSINESS_LOGO_MAX_BYTES) {
    return { ok: false, cleanFileName, contentType, message: 'Logo kan maks være 1 MB.' };
  }
  return { ok: true, cleanFileName, contentType };
};

const businessLogoStoragePath = ({ businessId = '', logoId = '', cleanFileName = '' } = {}) =>
  `businesses/${String(businessId || '').trim()}/logos/${String(logoId || '').trim()}-${safeFilePart(cleanFileName || 'firmalogo')}`;

const uploadBusinessLogo = async (file) => {
  if (!(file instanceof File)) throw new Error(t('svc.pickImageFile'));
  const logoFile = normalizeBusinessLogoFile(file);
  if (!logoFile.ok) throw new Error(logoFile.message);
  await initFirebase();
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  if (!runtime.activeBusinessId) throw new Error(t('svc.missingActiveBusiness'));
  if (!(runtime.user?.uid || runtime.auth?.currentUser?.uid)) throw new Error(t('svc.needLoginLogo'));
  const cleanFileName = logoFile.cleanFileName;
  const logoId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `logo-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
  const storagePath = businessLogoStoragePath({ businessId: runtime.activeBusinessId, logoId, cleanFileName });
  const ref = runtime.storage.ref().child(storagePath);
  await ref.put(file, {
    contentType: logoFile.contentType,
    contentDisposition: `inline; filename*=UTF-8''${encodeURIComponent(file.name || cleanFileName)}`,
    customMetadata: {
      originalName: file.name || cleanFileName,
      businessId: runtime.activeBusinessId || '',
      source: 'plumme-settings',
    },
  });
  const downloadUrl = await ref.getDownloadURL().catch(() => '');
  const logo = stripUndefinedDeep({
    id: logoId,
    name: cleanFileName,
    mimeType: logoFile.contentType,
    storagePath,
    downloadUrl: downloadUrl || undefined,
    uploadedAt: nowIso(),
  });
  try {
    await updateStateAndPersist((state) => ({
      ...state,
      settings: {
        ...(state.settings || {}),
        logoId,
        logoName: cleanFileName,
        logoMimeType: logoFile.contentType,
        logoStoragePath: storagePath,
        logoDownloadUrl: downloadUrl || '',
        logoUpdatedAt: nowIso(),
      },
    }));
  } catch (error) {
    // Do not leak an orphaned object when the following Firestore state write is
    // rejected. The original error is more useful to the user than cleanup errors.
    await ref.delete().catch(() => undefined);
    throw error;
  }
  return logo;
};

const getBusinessLogoDownloadUrl = async (storagePath = '') => {
  await initFirebase();
  const settings = mergeState(runtime.appState).settings || {};
  const userId = runtime.user?.uid || runtime.auth?.currentUser?.uid || '';
  const requested = String(storagePath || '').trim();
  const path = requested.includes('/')
    ? requested
    : String(settings.logoStoragePath || (requested && userId ? `users/${userId}/logos/${requested}` : settings.logoId && userId ? `users/${userId}/logos/${settings.logoId}` : '')).trim();
  if (!path) return '';
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  return runtime.storage.ref().child(path).getDownloadURL();
};

const deleteBusinessLogo = async () => {
  const settings = mergeState(runtime.appState).settings;
  const storagePath = settings.logoStoragePath || '';
  const userId = runtime.user?.uid || runtime.auth?.currentUser?.uid || '';
  const legacyPath = settings.logoId && userId ? `users/${userId}/logos/${settings.logoId}` : '';
  if (storagePath && runtime.storage) {
    await runtime.storage.ref().child(storagePath).delete().catch(() => undefined);
  }
  if (legacyPath && legacyPath !== storagePath && runtime.storage) {
    await runtime.storage.ref().child(legacyPath).delete().catch(() => undefined);
  }
  await updateStateAndPersist((state) => ({
    ...state,
    settings: {
      ...(state.settings || {}),
      logoId: '',
      logoName: '',
      logoMimeType: '',
      logoStoragePath: '',
      logoDownloadUrl: '',
      logoUpdatedAt: nowIso(),
    },
  }));
};

const sendPasswordResetEmail = async (email) => {
  await initFirebase();
  const target = String(email || runtime.auth?.currentUser?.email || '').trim();
  if (!target) throw new Error(t('svc.missingEmailAddress'));
  await runtime.auth.sendPasswordResetEmail(target);
};

const getEmployeePortals = async () => {
  if (runtime.auth?.currentUser && runtime.auth.currentUser.emailVerified !== true) {
    const error = new Error(t('err.auth.emailNotVerified', 'Bekreft e-postadressen din først.'));
    error.domainCode = 'auth.emailNotVerified';
    error.status = 412;
    throw error;
  }
  const payload = await authorizedPost('getEmployeePortalHttp');
  return safeArray(payload.portals);
};

const PERSONAL_FINANCE_IMPORT_MIME_TYPES = new Set([
  'application/pdf',
  'text/csv',
  'application/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

const ACCOUNTING_IMPORT_MIME_TYPES = new Set([
  'application/zip',
  'application/x-zip-compressed',
  'application/octet-stream',
  'application/xml',
  'text/xml',
  'text/csv',
  'application/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

const assertPersonalFinanceImportFile = (file) => {
  if (!file) throw new Error(t('svc.pickFileFirst'));
  const extension = String(file.name || '').split('.').pop().toLowerCase();
  const isAllowed = ['pdf', 'csv', 'xls', 'xlsx'].includes(extension)
    || PERSONAL_FINANCE_IMPORT_MIME_TYPES.has(file.type || '');
  if (!isAllowed) throw new Error(t('svc.unsupportedFileType'));
  if (Number(file.size || 0) <= 0 || Number(file.size || 0) > 15 * 1024 * 1024) {
    throw new Error(t('svc.fileSize15'));
  }
};
